import { createFeature, createReducer, on } from '@ngrx/store';
import { Prospect } from '@niko-crm/ui';
import { ProspectKanbanActions } from './prospect-kanban.actions';
import { ProspectActions } from '../prospects/prospect.actions';

export interface KanbanColumnState {
  ids: string[];
  entities: Record<string, Prospect>;
  page: number;
  limit: number;
  total: number;
  loading: boolean;
  hasMore: boolean;
  error: string | null;
}

export interface ProspectsKanbanState {
  columns: Record<string, KanbanColumnState>;
}

const initialState: ProspectsKanbanState = { columns: {} };

const emptyColumn: KanbanColumnState = {
  ids: [],
  entities: {},
  page: 0,
  limit: 25,
  total: 0,
  loading: false,
  hasMore: true,
  error: null,
};

export const prospectsKanbanReducer = createReducer(
  initialState,
  on(ProspectKanbanActions.loadKanbanColumn, (state, { stageId }) => ({
    ...state,
    columns: {
      ...state.columns,
      [stageId]: { ...(state.columns[stageId] ?? emptyColumn), loading: true, error: null },
    },
  })),
  on(ProspectKanbanActions.loadKanbanColumnSuccess, (state, { stageId, result, append }) => {
    const prev = state.columns[stageId] ?? emptyColumn;
    const entities = append ? { ...prev.entities } : {};
    for (const p of result.data) entities[p.id] = p;
    const ids = append ? [...prev.ids, ...result.data.map((p) => p.id)] : result.data.map((p) => p.id);
    return {
      ...state,
      columns: {
        ...state.columns,
        [stageId]: {
          ...prev,
          ids,
          entities,
          page: result.page,
          limit: result.limit,
          total: result.total,
          loading: false,
          hasMore: ids.length < result.total,
        },
      },
    };
  }),
  on(ProspectKanbanActions.loadKanbanColumnFailure, (state, { stageId, error }) => ({
    ...state,
    columns: {
      ...state.columns,
      [stageId]: { ...(state.columns[stageId] ?? emptyColumn), loading: false, error },
    },
  })),
  on(ProspectKanbanActions.resetKanbanColumns, () => initialState),
  on(ProspectKanbanActions.moveProspectStage, (state, { prospect, fromStageId, toStageId }) => {
    const from = state.columns[fromStageId];
    const to = state.columns[toStageId];
    if (!from || !to) return state;

    const { [prospect.id]: _removed, ...fromEntities } = from.entities;
    const moved: Prospect = { ...prospect, stageId: toStageId };

    return {
      ...state,
      columns: {
        ...state.columns,
        [fromStageId]: {
          ...from,
          ids: from.ids.filter((id) => id !== prospect.id),
          entities: fromEntities,
          total: Math.max(0, from.total - 1),
        },
        [toStageId]: {
          ...to,
          ids: [prospect.id, ...to.ids],
          entities: { ...to.entities, [prospect.id]: moved },
          total: to.total + 1,
        },
      },
    };
  }),
  on(ProspectKanbanActions.moveProspectStageSuccess, (state, { prospect, toStageId }) => {
    const to = state.columns[toStageId];
    if (!to || !(prospect.id in to.entities)) return state;
    return {
      ...state,
      columns: {
        ...state.columns,
        [toStageId]: { ...to, entities: { ...to.entities, [prospect.id]: prospect } },
      },
    };
  }),
  on(ProspectKanbanActions.moveProspectStageFailure, (state, { prospect, fromStageId, toStageId }) => {
    const from = state.columns[fromStageId];
    const to = state.columns[toStageId];
    if (!from || !to) return state;

    const { [prospect.id]: _removed, ...toEntities } = to.entities;

    return {
      ...state,
      columns: {
        ...state.columns,
        [toStageId]: {
          ...to,
          ids: to.ids.filter((id) => id !== prospect.id),
          entities: toEntities,
          total: Math.max(0, to.total - 1),
        },
        [fromStageId]: {
          ...from,
          ids: [prospect.id, ...from.ids],
          entities: { ...from.entities, [prospect.id]: prospect },
          total: from.total + 1,
        },
      },
    };
  }),
  // Mirrors edits from the shared `prospects` slice (e.g. edit dialog) so the card doesn't go stale.
  on(ProspectActions.updateProspectSuccess, (state, { prospect }) => {
    const currentStageId = Object.keys(state.columns).find(
      (stageId) => prospect.id in state.columns[stageId].entities,
    );
    if (!currentStageId) return state;

    const current = state.columns[currentStageId];
    if (prospect.stageId === currentStageId) {
      return {
        ...state,
        columns: {
          ...state.columns,
          [currentStageId]: { ...current, entities: { ...current.entities, [prospect.id]: prospect } },
        },
      };
    }

    // Stage changed from outside the drag flow: move it if the destination column is loaded.
    const { [prospect.id]: _removed, ...fromEntities } = current.entities;
    const updatedFrom: KanbanColumnState = {
      ...current,
      ids: current.ids.filter((id) => id !== prospect.id),
      entities: fromEntities,
      total: Math.max(0, current.total - 1),
    };
    const destination = prospect.stageId ? state.columns[prospect.stageId] : undefined;
    if (!destination) {
      return { ...state, columns: { ...state.columns, [currentStageId]: updatedFrom } };
    }
    return {
      ...state,
      columns: {
        ...state.columns,
        [currentStageId]: updatedFrom,
        [prospect.stageId as string]: {
          ...destination,
          ids: [prospect.id, ...destination.ids],
          entities: { ...destination.entities, [prospect.id]: prospect },
          total: destination.total + 1,
        },
      },
    };
  }),
);

export const prospectsKanbanFeature = createFeature({
  name: 'prospectsKanban',
  reducer: prospectsKanbanReducer,
});
